import type { CSSProperties } from 'react';
import Colours from '@shared/ui/atoms/Colours';

// Binned single-hue scale for occupancy in the brand blue: two tints, the brand
// blue itself, a shade, then dark blue. Checked as an ordinal ramp (monotone
// lightness, visible steps, light end clears a white surface). Orange marks
// over-capacity, grey marks nobody in, and grey hatching marks no capacity to
// compare against. Ink is whichever of dark text / white reads better on the fill.
// Fills stay as hex because the floor plan appends an alpha.

const DARK_INK = Colours.black;
const LIGHT_INK = '#ffffff';

export interface HeatBin {
  key: string;
  label: string;
  fill: string;
  ink: string;
  // Drawn over the fill
  texture?: string;
  // For outlined marks on the floor plan, when the fill would not show
  edge?: string;
}

export const EMPTY_BIN: HeatBin = { key: 'empty', label: '0%', fill: Colours.lightGrey, ink: DARK_INK };

const BINS: { upTo: number; bin: HeatBin }[] = [
  { upTo: 0.2, bin: { key: '1-20', label: '1-20%', fill: '#87b0ff', ink: DARK_INK } },
  { upTo: 0.4, bin: { key: '21-40', label: '21-40%', fill: '#538dff', ink: DARK_INK } },
  // Colours.blue, which is written as rgba
  { upTo: 0.6, bin: { key: '41-60', label: '41-60%', fill: '#0057ff', ink: LIGHT_INK } },
  { upTo: 0.8, bin: { key: '61-80', label: '61-80%', fill: '#0043ca', ink: LIGHT_INK } },
  { upTo: 1.0, bin: { key: '81-100', label: '81-100%', fill: Colours.darkBlue, ink: LIGHT_INK } },
];

export const OVER_CAPACITY_BIN: HeatBin = { key: 'over', label: 'Over 100%', fill: Colours.orange, ink: DARK_INK };

export const NO_CAPACITY_BIN: HeatBin = {
  key: 'no-capacity', label: 'No capacity', fill: '#ffffff', ink: DARK_INK, edge: Colours.mildGrey,
  texture: `repeating-linear-gradient(135deg, ${Colours.mildGrey} 0 1px, transparent 1px 6px)`,
};

export const RAMP_BINS: HeatBin[] = BINS.map(b => b.bin);

// ratio is people / capacity. undefined means there is no capacity to compare against.
export function heatBin(ratio: number | undefined): HeatBin {
  if (ratio === undefined) {
    return NO_CAPACITY_BIN;
  }
  if (ratio <= 0) {
    return EMPTY_BIN;
  }
  const match = BINS.find(b => ratio <= b.upTo);
  return match ? match.bin : OVER_CAPACITY_BIN;
}

export function formatPercent(ratio: number | undefined): string {
  if (ratio === undefined) return '-';
  if (ratio > 0 && ratio < 0.005) return '<1%';
  return `${Math.round(ratio * 100)}%`;
}

export function binStyle(bin: HeatBin): CSSProperties {
  return { backgroundColor: bin.fill, backgroundImage: bin.texture, color: bin.ink };
}
